import { Component, inject, Input, OnInit, signal, ViewChild } from '@angular/core';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../../@shared/components/table-component/table-component';
import { Params } from '../../../../services/utilis/add-params-service';
import { CustomerActivity } from '../../../../models/customer-activity';
import { CustomerService } from '../../../../services/customers/customer-service';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { enumToOptions } from '../../../../@shared/utility/enumToOptions';
import { ActivityType } from '../../../../models/enums';

@Component({
  selector: 'app-customer-details-activity-component',
  imports: [TableComponent],
  templateUrl: './customer-details-activity-component.html',
  styleUrl: './customer-details-activity-component.scss'
})
export class CustomerDetailsActivityComponent implements OnInit {

  @ViewChild(TableComponent) table!: TableComponent<CustomerActivity>;
  @Input() customerID: string | undefined

  params = [
    "page",
    "size",
    'sortBy',
    'sortDirection',
    'sortDirection',
    'activityType',
    'createdFrom',
    'createdTo',
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    activityType: "activityType",
    createdFrom: "createdFrom",
    createdTo: "createdTo",
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<CustomerActivity>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    {
      header: 'Activity Type',
      field: 'activityType',
      sortable: true,
      isCapitalize: true,
      isFilterable: true,
      filterType: 'multiselect',
      filterOptions: enumToOptions(ActivityType),
    },
    { header: 'Customer ID', field: 'customerId', sortable: true },
    { header: 'Ride ID', field: 'rideId', sortable: true },
    { header: 'Created At', field: 'createdAt', sortable: true, isDateTime: true },
  ];

  customerActivityList = signal<CustomerActivity[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  createdDateFilter: CustomFilter = {
    key: "created",
    label: "Date",
    type: "date-range",
    options: []
  }

  private lastParams = new BehaviorSubject<Record<string, unknown> | null>(null);

  private customerService = inject(CustomerService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getCustomerActivityList()
  }

  getCustomerActivityList = () => {
    this.activatedRoute.queryParams.pipe(
      debounceTime(0),
      filter(params => params['tab'] === '1'),
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
            .filter(([key]) => this.params.includes(key))
            .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      filter((queryParams) => {
        // 🔥 only continue if params changed
        if (JSON.stringify(this.lastParams.value) === JSON.stringify(queryParams)) {
          return false;
        }
        this.lastParams.next(queryParams);
        return true;
      }),

      tap((queryParams) => {
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'CreatedAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        return this.customerService.getCustomersActivityList(this.customerID!, params);
      })
    )
      .subscribe((res) => {
        this.customerActivityList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  resetAll = () => {
    this.table?.clearAllFilters();
  }
}
